import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import BookmarkRoundedIcon from '@mui/icons-material/BookmarkRounded'
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useAppBase, appPath } from '../components/appBase'
import { eventService } from '../services/eventService'
import { track } from '../services/telemetry'
import type { Session, Speaker } from '../data/types'
import { formatDayLabel, formatTimeRange } from '../utils/format'
import PageContainer from '../components/ui/PageContainer'
import { EmptyState, LoadingState } from '../components/ui/States'
import SessionTypeChip from '../components/ui/SessionTypeChip'
import SessionLive from '../components/SessionLive'
import { useAppData } from '../contexts/AppDataContext'
import { resolveAssetUrl } from '../services/assetUrl'

export default function SessionDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const base = useAppBase()
  const { isSaved, toggleSavedSession } = useAppData()
  const [session, setSession] = useState<Session | undefined>()
  const [speakers, setSpeakers] = useState<Speaker[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([eventService.getSession(id), eventService.getSpeakers()]).then(
      ([s, sp]) => {
        setSession(s)
        setSpeakers(sp)
        setLoading(false)
        if (s) track('session_view', { entityType: 'session', entityId: s.id, metadata: { title: s.title } })
      },
    )
  }, [id])

  if (loading) return <LoadingState />
  if (!session)
    return (
      <PageContainer>
        <EmptyState
          title="Session not found"
          description="This session may have been moved or removed."
          action={
            <Button variant="contained" onClick={() => navigate(appPath(base, 'agenda'))}>
              Back to agenda
            </Button>
          }
        />
      </PageContainer>
    )

  const saved = isSaved(session.id)
  const sessionSpeakers = speakers.filter((s) => session.speakerIds.includes(s.id))
  const canSave = session.type !== 'break'

  return (
    <PageContainer maxWidth="md">
      <Button
        startIcon={<ArrowBackRoundedIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2, color: 'text.secondary' }}
      >
        Back
      </Button>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <SessionTypeChip type={session.type} />
        {session.track && (
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {session.track}
          </Typography>
        )}
      </Stack>

      <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
        {session.title}
      </Typography>

      <Stack spacing={1.5} sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <ScheduleRoundedIcon color="primary" />
          <Box>
            <Typography variant="body1" fontWeight={600}>
              {formatTimeRange(session.startsAt, session.endsAt)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDayLabel(session.startsAt)}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <PlaceRoundedIcon color="primary" />
          <Typography variant="body1" fontWeight={600}>
            {session.location}
          </Typography>
        </Stack>
      </Stack>

      {canSave && (
        <Button
          variant={saved ? 'outlined' : 'contained'}
          startIcon={saved ? <BookmarkRoundedIcon /> : <BookmarkBorderRoundedIcon />}
          onClick={() => toggleSavedSession(session.id)}
          sx={{ mb: 3 }}
        >
          {saved ? 'Saved to my schedule' : 'Add to my schedule'}
        </Button>
      )}

      {session.description && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom>
            About this session
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {session.description}
          </Typography>
        </>
      )}

      {canSave && <SessionLive sessionId={session.id} />}

      {sessionSpeakers.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom>
            {sessionSpeakers.length > 1 ? 'Speakers' : 'Speaker'}
          </Typography>
          <Stack spacing={1.5}>
            {sessionSpeakers.map((sp) => (
              <Card
                key={sp.id}
                variant="outlined"
                component={Link}
                to={appPath(base, `speakers/${sp.id}`)}
                sx={{
                  p: 2,
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  borderRadius: 3,
                  textDecoration: 'none',
                  color: 'inherit',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: sp.avatarColor,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    flexShrink: 0,
                    overflow: 'hidden',
                    backgroundImage: sp.photoUrl
                      ? `url(${resolveAssetUrl(sp.photoUrl)})`
                      : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  {sp.photoUrl ? '' : sp.initials}
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {sp.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {sp.title}, {sp.company}
                  </Typography>
                </Box>
              </Card>
            ))}
          </Stack>
        </>
      )}
    </PageContainer>
  )
}
